import { useState } from 'react'
import { PLATFORMS, CHANGE_TYPES, releasesFor } from './updates.js'
import {
  AppleSolid,
  WindowsSolid,
  CheckCircleSolid,
  ArrowUpRightSolid,
  ChangeTypeIcon,
} from './components/SolidIcons.jsx'

function PlatformIcon({ id, className }) {
  return id === 'macos' ? <AppleSolid className={className} /> : <WindowsSolid className={className} />
}

function StatusBadge({ status }) {
  if (status === 'building') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inset-0 animate-ping rounded-full bg-amber-400 opacity-80" />
          <span className="relative inline-block h-2 w-2 rounded-full bg-amber-500" />
        </span>
        Being built
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700">
      <CheckCircleSolid className="h-3.5 w-3.5" />
      Shipped
    </span>
  )
}

function ChangeRow({ change }) {
  return (
    <li className="flex items-start gap-2.5">
      <ChangeTypeIcon type={change.type} className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
      <span className="text-pretty text-[13px] leading-relaxed text-neutral-600">
        <span className="font-semibold text-neutral-500">{CHANGE_TYPES[change.type]?.label ?? ''}</span>
        <span className="mx-1.5 text-neutral-300">·</span>
        {change.text}
      </span>
    </li>
  )
}

function ReleaseCard({ release }) {
  return (
    <div className="rounded-2xl border border-black/[0.04] bg-white p-6 md:p-7">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
          <PlatformIcon id={release.platform} className="h-3.5 w-3.5" />
          {PLATFORMS[release.platform].label}
        </span>
        <span className="text-lg font-extrabold tracking-[-0.04em] text-neutral-900">v{release.version}</span>
        <StatusBadge status={release.status} />
        {release.date && (
          <span className="ml-auto text-[12px] text-neutral-400">{release.date}</span>
        )}
      </div>

      {release.summary && (
        <p className="mb-5 text-pretty text-[14px] leading-relaxed text-neutral-700">{release.summary}</p>
      )}

      <ul className="space-y-2.5">
        {release.changes.map((change, i) => (
          <ChangeRow key={i} change={change} />
        ))}
      </ul>

      {release.link && (
        <a
          href={release.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-1 text-[13px] font-semibold text-neutral-500 no-underline transition-colors hover:text-neutral-900"
        >
          Details
          <ArrowUpRightSolid className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  )
}

function PlatformColumn({ platformId }) {
  const releases = releasesFor(platformId)
  if (releases.length === 0) return null
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <PlatformIcon id={platformId} className="h-5 w-5 text-neutral-900" />
        <h2 className="text-[15px] font-bold tracking-tight text-neutral-900">{PLATFORMS[platformId].label}</h2>
      </div>
      {releases.map((release) => (
        <ReleaseCard key={`${release.platform}-${release.version}`} release={release} />
      ))}
    </section>
  )
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'macos', label: 'macOS' },
  { id: 'windows', label: 'Windows' },
]

export default function UpdatesPage() {
  const [filter, setFilter] = useState('all')
  const showMac = filter === 'all' || filter === 'macos'
  const showWin = filter === 'all' || filter === 'windows'

  return (
    <article className="mx-auto max-w-[640px] px-5 py-16 tracking-tight md:px-10 md:py-24">
      <div className="mb-8">
        <h1 className="mb-3 text-3xl font-extrabold leading-tight tracking-[-1.5px] text-neutral-900 md:text-5xl">
          Updates
        </h1>
        <p className="max-w-xl text-[15px] leading-relaxed text-neutral-600">
          Every change to Keeby, tracked here. macOS and Windows ship on their own
          schedules, so their updates are listed separately.
        </p>
      </div>

      {/* Platform filter — segmented control */}
      <div className="mb-10 inline-flex rounded-full border border-black/[0.06] bg-neutral-100 p-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${
              filter === f.id ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {f.id === 'macos' && <AppleSolid className="h-3.5 w-3.5" />}
            {f.id === 'windows' && <WindowsSolid className="h-3.5 w-3.5" />}
            {f.label}
          </button>
        ))}
      </div>

      {/* Single column; platforms stack (macOS, then Windows). */}
      <div className="space-y-12">
        {showMac && <PlatformColumn platformId="macos" />}
        {showWin && <PlatformColumn platformId="windows" />}
      </div>
    </article>
  )
}
